import { FormProvider, useForm, Controller } from "react-hook-form"
import { api_getPlayerRoster, api_getEltaWarroomData } from "../API/axios"
import { useEffect, useState } from "react"
import { resolvePlayerIDMap } from "../Utils/HeadToHeadUtils"
import { resolveBatterLiveCardData, resolvePitcherLiveCardData } from "../Utils/OurSportsUtils"
import StatsInsight from '../style/images/logo.png'
import OurSport from '../style/images/tsl_logo.png'
import ELTA from '../style/images/ELTA.png'
import { EmptyFrame, LoadingFrame } from "../Components/PersonFrame"
import { FormSelectionBox } from "../Components/InputBox"
import { useNavigate } from 'react-router-dom'
import { DatePicker } from "@mui/x-date-pickers/DatePicker"

const PitchNameMap = {
  FB: '直球',
  CB: '曲球',
  SL: '滑球',
  CH: '變速球',
  SC: '慢速曲球',
  SP: '指叉球',
  KN: '蝴蝶球',
  CT: '卡特球',
  SFF: '叉指變速球',
  FT: '二縫線',
  OT: '其他'
}

// 創建一個處理百分比格式的函數
const formatPercentage = (value) => {
  if (!value) return "0%";
  // 移除末尾的點，確保格式正確
  return value.endsWith('%') ? value.replace(/\.%$/, '%') : value.replace(/\.$/, '') + "%";
};

// 添加一個專門處理打擊率格式的函數
const formatBattingAverage = (value) => {
  if (!value) return ".000";
  
  // 如果值已經是百分比格式，先轉換回小數
  if (typeof value === 'string' && value.endsWith('%')) {
    const numValue = parseFloat(value) / 100;
    return numValue.toFixed(3).substring(1); // 取小數點後三位，並去掉前面的0
  }
  
  // 如果值是小數格式，直接格式化
  if (typeof value === 'number' || !isNaN(parseFloat(value))) {
    const numValue = parseFloat(value);
    return numValue.toFixed(3).substring(1); // 取小數點後三位，並去掉前面的0
  }
  
  // 如果是其他格式，返回默認值
  return ".000";
};

const DateIntervalSelector = ({name, label, defaultValue, control}) => {
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={
        ({ field }) =>
          <DatePicker
            label={label}
            {...field}
            sx={{
              '& .MuiInputBase-input': { 
                fontSize: '2rem',  // 增加字體大小
              },
              '& .MuiOutlinedInput-root': {
                height: '38px',    // 確保高度與其他輸入框一致
                backgroundColor: '#f5f5f5', // 設置與其他輸入框相同的背景色
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#ccc', // 設置邊框顏色
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#aaa', // 懸停時的邊框顏色
              },
              width: '100%',       // 確保寬度填滿容器
            }}
            slotProps={{
              popper: {
                sx: {
                  // 增加日曆彈出框的大小
                  '& .MuiPickersCalendarHeader-root': {
                    fontSize: '2rem',
                  },
                  // 增加年月標題的字體大小
                  '& .MuiPickersCalendarHeader-label': {
                    fontSize: '2.5rem', // 年月標題更大
                    fontWeight: 'bold',
                  },
                  '& .MuiDayCalendar-header': {
                    fontSize: '2rem',
                  },
                  // 增加星期標題的大小
                  '& .MuiDayCalendar-weekDayLabel': {
                    fontSize: '2rem',
                    width: '40px',
                    height: '40px',
                  },
                  '& .MuiPickersDay-root': {
                    fontSize: '2rem',
                    width: '40px',
                    height: '40px',
                  },
                  '& .MuiPickersYear-yearButton': {
                    fontSize: '2rem',
                  },
                  '& .MuiPickersMonth-monthButton': {
                    fontSize: '2rem',
                  },
                  // 增加整個日曆的尺寸
                  '& .MuiDateCalendar-root': {
                    width: '400px', // 增加寬度以適應更大的星期標題
                    height: '400px', // 相應增加高度
                  },
                  // 增加箭頭按鈕的大小
                  '& .MuiPickersArrowSwitcher-button': {
                    fontSize: '2rem',
                    '& svg': {
                      width: '2rem',
                      height: '2rem',
                    }
                  }
                }
              }
            }}
          />
      }
    />
  )
}

const EltaRawData = () => {
  const form = useForm()
  const [roster, setRoster] = useState({ teams: [], options: {}, map: {} })
  const [playerData, setPlayerData] = useState({zoneData: {left: {}, right: {}, all: {}}, usageData: { usage: { all: [] } }, locationData: [], densityData: []})
  const [error, setError] = useState(true)
  const [loading, setLoading] = useState(false)
  const [type, setType] = useState('batter')
  const [queryType, setQueryType] = useState('year')
  const navigate = useNavigate()
  const handleOnSubmit = (result) => {
    setLoading(true);
    
    // 根據查詢類型構建請求參數
    let requestPayload = { ...result };
    
    if (queryType === 'dateRange') {
      // 時間區間模式：使用 startTime 和 endTime 構建 timespan
      const startDate = result.startTime;
      const endDate = result.endTime;
      
      // 格式化日期為 YYYY-MM-DD 格式
      const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      };
      
      // 替換 year 參數為 timespan
      delete requestPayload.year;
      delete requestPayload.startTime;
      delete requestPayload.endTime;
      
      requestPayload.timespan = {
        start: formatDate(startDate),
        end: formatDate(endDate)
      };
    } else {
      // 年份模式：確保 year 參數存在
      if (!requestPayload.year) {
        requestPayload.year = '2024'; // 設置默認年份，如果沒有選擇
      }
      
      // 刪除可能存在的 timespan、startTime 和 endTime 參數
      delete requestPayload.timespan;
      delete requestPayload.startTime;
      delete requestPayload.endTime;
    }
    
    console.log(requestPayload)
    // 調用 API
    api_getEltaWarroomData(requestPayload).then((res) => {
      let data = {};
      if (res.data.length === 0) {
        setError(true);
        setLoading(false);
        return;
      } 
      
      if (result.type === 'Batter') {
        data = resolveBatterLiveCardData(res.data);
      } else {
        data = resolvePitcherLiveCardData(res.data);
      }
      
      setError(false);
      setLoading(false);
      setType(result.type.toLowerCase());
      setPlayerData(data);
    }).catch(err => {
      console.error("API 調用錯誤:", err);
      setError(true);
      setLoading(false);
    });
  }
  
  const PlayerOptions = () => {
    const ttag = form.watch('Team')
    
    if (ttag === undefined || ttag === '') {
      return { options: [], optionTags: undefined }
    }
    return { options: roster.options[ttag], optionTags: roster.map[ttag] }
  }
  
  useEffect(() => {
    api_getPlayerRoster().then((res) => {
      const data = [...res.data]
      let processed = resolvePlayerIDMap(data)
      
      let teams = processed.teams.filter(team => team !== "隊伍");
      
      let final_roster = { teams: teams, options: {}, map: {} }
      for (const team of teams) {
        final_roster.options[team] = [ ...processed.options[team].pitcher, ...processed.options[team].batter ].filter((e) => !["全部投手","全部打者"].includes(e))
        final_roster.map[team] = { ...processed.map[team].pitcher, ...processed.map[team].batter }
      }
      setRoster(final_roster)
    })
  }, [])
  
  const handleOurSportClick = () => {
    navigate('/oursport');
  };

  const handleELTAClick = () => {
    navigate('/elta-raw-data');
  };

  return (
    <FormProvider {...form}>
      <div className="stream-card-wrapper">
        <div className="brand-header">
          <img src={StatsInsight} />
          <img 
            src={OurSport} 
            alt="OurSport" 
            onClick={handleOurSportClick} 
            style={{ cursor: 'pointer' }} 
          />
          <img 
            src={ELTA} 
            alt="ELTA" 
            onClick={handleELTAClick} 
            style={{ cursor: 'pointer' }} 
          />
        </div>
        <div className="stream-form">
          <form onSubmit={form.handleSubmit(handleOnSubmit)}>
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              width: '90%', 
              padding: '0 20px'
            }}>
              <div style={{ flex: '1.5' }}>
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setQueryType(queryType === 'year' ? 'dateRange' : 'year');
                  }}
                  className="submit-button"
                  style={{ 
                    height: '38px',  // 設置與其他輸入框相同的高度
                    width: '100%'    // 確保寬度填滿容器
                  }}
                >
                  {queryType === 'year' ? '以時間區間查詢' : '以年份查詢'}
                </button>
              </div>
              
              {queryType === 'year' ? (
                <div style={{ flex: '1.5' }}>
                  <FormSelectionBox 
                    name='year' 
                    options={['2022', '2023', '2024', '2025']} 
                    optionTags={undefined} 
                    placeholder="年份" 
                  />
                </div>
              ) : (
                <div style={{ flex: '3', display: 'flex', gap: '10px' }}>
                  <DateIntervalSelector 
                    name='startTime' 
                    label='' 
                    defaultValue={new Date('2025-01-01')} 
                    control={form.control}
                  />
                  <DateIntervalSelector 
                    name='endTime' 
                    label='' 
                    defaultValue={new Date()} 
                    control={form.control}
                  />
                </div>
              )}
              
              <div style={{ flex: '2' }}>
                <FormSelectionBox 
                  name='Team' 
                  options={roster.teams} 
                  optionTags={undefined} 
                  placeholder="隊伍" 
                />
              </div>
              <div style={{ flex: '2' }}>
                <FormSelectionBox 
                  name='id'
                  options={PlayerOptions().options} 
                  optionTags={PlayerOptions().optionTags} 
                  placeholder="請選擇選手" 
                />
              </div>
              <div style={{ flex: '1.5' }}>
                <FormSelectionBox 
                  name='type' 
                  options={["Pitcher", "Batter"]} 
                  optionTags={{Pitcher: "投球", Batter: "打擊"}} 
                  placeholder="投球/打擊"
                />
              </div>
              <div style={{ flex: '1' }}>
                <button type="submit" style={{ width: '100%', height: '38px' }}>查詢</button>
              </div>
            </div>
          </form>
        </div>
        {
          loading ? <LoadingFrame /> :
            <>
              {
                error ? <EmptyFrame message="無相關資料，請重新設定搜尋條件" /> :
                  <div className="stream-cards">
                    <div className="stream-card-row">
                      <div className="stream-card-item">
                        {
                          type === 'batter' ? (
                            <div>
                              
                              {/* 出棒率數據 */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                                {/* 總出棒率 */}
                                <div style={{ flex: 1 }}>
                                  <div className="stream-card-title">總出棒率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({"1": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["0"]),
                                      "2": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["1"]),
                                      "3": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["2"]),
                                      "4": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["3"]),
                                      "5": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["4"]),
                                      "6": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["5"]),
                                      "7": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["6"]),
                                      "8": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["7"]),
                                      "9": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["8"]),
                                      "10": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["9"]),
                                      "11": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["10"]),
                                      "12": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["11"]),
                                      "13": formatPercentage(playerData.zoneData.all.swing && playerData.zoneData.all.swing["12"])}, null, 2)}
                                  </pre>
                                </div>
                                
                                {/* 左投出棒率 */}
                                <div style={{ flex: 1 }}>
                                  <div className="stream-card-title">左投出棒率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({"1": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["0"]),
                                      "2": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["1"]),
                                      "3": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["2"]),
                                      "4": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["3"]),
                                      "5": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["4"]),
                                      "6": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["5"]),
                                      "7": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["6"]),
                                      "8": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["7"]),
                                      "9": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["8"]),
                                      "10": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["9"]),
                                      "11": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["10"]),
                                      "12": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["11"]),
                                      "13": formatPercentage(playerData.zoneData.left.swing && playerData.zoneData.left.swing["12"])}, null, 2)}
                                  </pre>
                                </div>
                                
                                {/* 右投出棒率 */}
                                <div style={{ flex: 1 }}>
                                  <div className="stream-card-title">右投出棒率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({"1": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["0"]),
                                      "2": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["1"]),
                                      "3": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["2"]),
                                      "4": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["3"]),
                                      "5": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["4"]),
                                      "6": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["5"]),
                                      "7": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["6"]),
                                      "8": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["7"]),
                                      "9": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["8"]),
                                      "10": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["9"]),
                                      "11": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["10"]),
                                      "12": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["11"]),
                                      "13": formatPercentage(playerData.zoneData.right.swing && playerData.zoneData.right.swing["12"])}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                                {/* 總被打擊率 */}
                                <div style={{ flex: 1 }}>
                                  <div className="stream-card-title">總被打擊率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({
                                      "1": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["0"]),
                                      "2": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["1"]),
                                      "3": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["2"]),
                                      "4": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["3"]),
                                      "5": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["4"]),
                                      "6": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["5"]),
                                      "7": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["6"]),
                                      "8": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["7"]),
                                      "9": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["8"]),
                                      "10": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["9"]),
                                      "11": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["10"]),
                                      "12": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["11"]),
                                      "13": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["12"])
                                    }, null, 2)}
                                  </pre>
                                </div>
                                
                                {/* 左打者被打擊率 */}
                                <div style={{ flex: 1.3 }}>
                                  <div className="stream-card-title">左打被打擊率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({
                                      "1": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["0"]),
                                      "2": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["1"]),
                                      "3": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["2"]),
                                      "4": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["3"]),
                                      "5": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["4"]),
                                      "6": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["5"]),
                                      "7": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["6"]),
                                      "8": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["7"]),
                                      "9": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["8"]),
                                      "10": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["9"]),
                                      "11": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["10"]),
                                      "12": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["11"]),
                                      "13": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["12"])
                                    }, null, 2)}
                                  </pre>
                                </div>
                                
                                {/* 右打者被打擊率 */}
                                <div style={{ flex: 1 }}>
                                  <div className="stream-card-title">右打被打擊率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({
                                      "1": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["0"]),
                                      "2": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["1"]),
                                      "3": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["2"]),
                                      "4": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["3"]),
                                      "5": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["4"]),
                                      "6": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["5"]),
                                      "7": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["6"]),
                                      "8": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["7"]),
                                      "9": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["8"]),
                                      "10": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["9"]),
                                      "11": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["10"]),
                                      "12": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["11"]),
                                      "13": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["12"])
                                    }, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          )
                        }
                      </div>
                    </div>
                    <div className="stream-card-row">
                      <div className="stream-card-item">
                        {
                          type === 'batter' ? (
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                                {/* 總打擊率 */}
                                <div style={{ flex: 1 }}>  {/* 調整寬度比例 */}
                                  <div className="stream-card-title">總打擊率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({
                                      "1": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["0"]),
                                      "2": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["1"]),
                                      "3": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["2"]),
                                      "4": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["3"]),
                                      "5": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["4"]),
                                      "6": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["5"]),
                                      "7": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["6"]),
                                      "8": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["7"]),
                                      "9": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["8"]),
                                      "10": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["9"]),
                                      "11": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["10"]),
                                      "12": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["11"]),
                                      "13": formatBattingAverage(playerData.zoneData.all.avg && playerData.zoneData.all.avg["12"])
                                    }, null, 2)}
                                  </pre>
                                </div>
                                
                                {/* 左投打擊率 */}
                                <div style={{ flex: 1.3 }}>  {/* 增加寬度比例，使其位於中央 */}
                                  <div className="stream-card-title">左投打擊率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({
                                      "1": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["0"]),
                                      "2": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["1"]),
                                      "3": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["2"]),
                                      "4": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["3"]),
                                      "5": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["4"]),
                                      "6": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["5"]),
                                      "7": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["6"]),
                                      "8": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["7"]),
                                      "9": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["8"]),
                                      "10": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["9"]),
                                      "11": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["10"]),
                                      "12": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["11"]),
                                      "13": formatBattingAverage(playerData.zoneData.left.avg && playerData.zoneData.left.avg["12"])
                                    }, null, 2)}
                                  </pre>
                                </div>
                                
                                {/* 右投打擊率 */}
                                <div style={{ flex: 1 }}>  {/* 調整寬度比例 */}
                                  <div className="stream-card-title">右投打擊率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({
                                      "1": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["0"]),
                                      "2": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["1"]),
                                      "3": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["2"]),
                                      "4": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["3"]),
                                      "5": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["4"]),
                                      "6": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["5"]),
                                      "7": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["6"]),
                                      "8": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["7"]),
                                      "9": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["8"]),
                                      "10": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["9"]),
                                      "11": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["10"]),
                                      "12": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["11"]),
                                      "13": formatBattingAverage(playerData.zoneData.right.avg && playerData.zoneData.right.avg["12"])
                                    }, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                                {/* 總被打擊率 */}
                                <div style={{ flex: 1.5 }}>
                                  <div className="stream-card-title">總進壘位置比率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({
                                      "1": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["0"]),
                                      "2": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["1"]),
                                      "3": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["2"]),
                                      "4": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["3"]),
                                      "5": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["4"]),
                                      "6": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["5"]),
                                      "7": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["6"]),
                                      "8": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["7"]),
                                      "9": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["8"]),
                                      "10": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["9"]),
                                      "11": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["10"]),
                                      "12": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["11"]),
                                      "13": formatPercentage(playerData.zoneData.all.pitch && playerData.zoneData.all.pitch["12"])
                                    }, null, 2)}
                                  </pre>
                                </div>
                                
                                {/* 左打者被打擊率 */}
                                <div style={{ flex: 1.5 }}>
                                  <div className="stream-card-title">對左打進壘位置比率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({
                                      "1": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["0"]),
                                      "2": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["1"]),
                                      "3": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["2"]),
                                      "4": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["3"]),
                                      "5": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["4"]),
                                      "6": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["5"]),
                                      "7": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["6"]),
                                      "8": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["7"]),
                                      "9": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["8"]),
                                      "10": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["9"]),
                                      "11": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["10"]),
                                      "12": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["11"]),
                                      "13": formatPercentage(playerData.zoneData.left.pitch && playerData.zoneData.left.pitch["12"])
                                    }, null, 2)}
                                  </pre>
                                </div>
                                
                                {/* 右打者被打擊率 */}
                                <div style={{ flex: 1.5 }}>
                                  <div className="stream-card-title">對右打進壘位置比率</div>
                                  <pre style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    borderRadius: '5px',
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    textAlign: 'left',
                                    fontSize: '0.85em'
                                  }}>
                                    {JSON.stringify({
                                      "1": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["0"]),
                                      "2": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["1"]),
                                      "3": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["2"]),
                                      "4": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["3"]),
                                      "5": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["4"]),
                                      "6": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["5"]),
                                      "7": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["6"]),
                                      "8": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["7"]),
                                      "9": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["8"]),
                                      "10": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["9"]),
                                      "11": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["10"]),
                                      "12": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["11"]),
                                      "13": formatPercentage(playerData.zoneData.right.pitch && playerData.zoneData.right.pitch["12"])
                                    }, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          )
                        }
                      </div>
                    </div>
                    <div className="stream-card-row">
                      <div className="stream-card-item">
                        {
                          type === 'batter' ? (
                            <div>
                              <div className="stream-card-title">擊球分佈比率</div>
                              <pre style={{ 
                                backgroundColor: '#f5f5f5', 
                                padding: '10px', 
                                borderRadius: '5px',
                                overflow: 'auto',
                                maxHeight: '300px',
                                textAlign: 'left',
                                fontSize: '0.85em'
                              }}>
                                {JSON.stringify({
                                  "11": playerData.locationData && playerData.locationData[0] ? playerData.locationData[0] + "%" : "0%",
                                  "12": playerData.locationData && playerData.locationData[1] ? playerData.locationData[1] + "%" : "0%",
                                  "13": playerData.locationData && playerData.locationData[2] ? playerData.locationData[2] + "%" : "0%",
                                  "14": playerData.locationData && playerData.locationData[3] ? playerData.locationData[3] + "%" : "0%",
                                  "15": playerData.locationData && playerData.locationData[4] ? playerData.locationData[4] + "%" : "0%",
                                  "16": playerData.locationData && playerData.locationData[5] ? playerData.locationData[5] + "%" : "0%",
                                  "1": playerData.locationData && playerData.locationData[6] ? playerData.locationData[6] + "%" : "0%",
                                  "2": playerData.locationData && playerData.locationData[7] ? playerData.locationData[7] + "%" : "0%",
                                  "3": playerData.locationData && playerData.locationData[8] ? playerData.locationData[8] + "%" : "0%",
                                  "4": playerData.locationData && playerData.locationData[9] ? playerData.locationData[9] + "%" : "0%",
                                  "5": playerData.locationData && playerData.locationData[10] ? playerData.locationData[10] + "%" : "0%"
                                }, null, 2)}
                              </pre>
                            </div>
                          ) : (
                            <div>
                              <div className="stream-card-title">球種使用比例</div>
                              <pre style={{ 
                                backgroundColor: '#f5f5f5', 
                                padding: '10px', 
                                borderRadius: '5px',
                                overflow: 'auto',
                                maxHeight: '300px',
                                textAlign: 'left',
                                fontSize: '0.85em'
                              }}>
                                {JSON.stringify(
                                  playerData.usageData.usage.all.reduce((acc, e) => {
                                    acc[PitchNameMap[e.tag]] = formatPercentage(e.usage);
                                    return acc;
                                  }, {}), 
                                  null, 2
                                )}
                              </pre>
                            </div>
                          )
                        }
                      </div>
                    </div>
                  </div>
              }
            </>
        }
      </div>
    </FormProvider>
  )
}

export default EltaRawData